import { List, Observation } from "@medplum/fhirtypes";

/** Gestational age expressed as whole weeks plus remaining days. */
export interface Gestation {
  weeks: number;
  days: number;
}

export interface PregnancyRecord {
  patientId: string;
  list: List;
  /**
   * true  = current/ongoing pregnancy  (List.status === "current")
   * false = past pregnancy        (List.status === "retired")
   */
  isActive: boolean;
  edd: string | undefined;
  lastMenstrualPeriod: string | undefined;
  gestationalAge: Gestation | undefined;
  /** Delivery/close-out date anchor for retired pregnancies. */
  deliveryDate?: string;
  /** Postpartum age snapshot at build/save time; display should recompute from deliveryDate. */
  postpartumAge?: Gestation;
  startDateTime: string;
  /** Will be undefined if pregnancy is active. */
  endDateTime?: string;
}

/** Input parameters for building EDD-related FHIR bundle entries. */
export interface EDDParams {
  /** When `true`, always creates a new pregnancy record without checking for an existing one. */
  closeExistingActiveRecord?: boolean;
  /** Which EDD source to set as active; defaults to `EDDDisplay.LMP`. */
  eddDisplay?: EDDDisplay;
  /** ISO date string for the last menstrual period. */
  lastMenstrualPeriod?: string;
  /** Reliability of the LMP date (e.g. `"certain"`, `"approximate"`). */
  lastMenstrualPeriodReliability?: string;
  /** ISO date string for the ultrasound exam. */
  ultrasoundDate?: string;
  /** Whole weeks of gestational age measured at the ultrasound. */
  ultrasoundGestationalAgeWeeks?: number;
  /** Additional days of gestational age beyond the full weeks. */
  ultrasoundGestationalAgeDays?: number;
  /** Pre-calculated EDD ISO date string from the ultrasound (used by the EDD bot). */
  ultrasoundEDDDate?: string;
  /** ISO date string for a manually entered custom EDD. */
  customEDDDate?: string;
  /** Resource(s) this data was derived from (e.g. a `QuestionnaireResponse`). */
  derivedFrom?: Observation["derivedFrom"];
}

/** Identifies which EDD calculation method is currently displayed to the user. */
export enum EDDDisplay {
  LMP = "forms/edd/display/lmp",
  Ultrasound = "forms/edd/display/us",
  Custom = "forms/edd/display/custom",
}
